import React, { useEffect, useState } from 'react';
import {
  onSave,
  currentDate,
  getCurrentTime,
  getOptionLabel,
  useNotification,
  fieldValidation,
  dropDownValidation,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import * as yup from 'yup';
import {
  SignatureBlock,
  SpedNoteHeading,
  formatServiceDateTime,
} from '../spedNotesCommonUtils';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { postSpedNote } from '../../services/spedNotesApi';
import { SpedGridForm, SpedPaper } from './spedNotesStyled';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { getSearchedStudents } from 'layouts/students/service/studentsApi';
import { requiredField, spedNoteSuccess, disablePastDate } from 'constants';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';
import { usePermissionCustomHook } from 'layouts/users/userPermissions/utils/permissionsUtils';

const AddSpedNote = (props) => {
  const { filterOptions } = props;
  const dispatch = useDispatch();
  const canEditSpedNotes = usePermissionCustomHook('CAN_EDIT_SPED_NOTES');
  const canAccessSpedNotes = usePermissionCustomHook('CAN_ACCESS_SPED_NOTES');
  const { NotificationPopup, setOpenNotification, setNotificationMessage } =
    useNotification();

  const signatureTextValidation = yup
    .string()
    .required(requiredField)
    .matches(/^[a-zA-Z\s]+$/, 'This signature must contain only letters');

  const signatureAgreeValidation = yup
    .boolean()
    .oneOf([true], '*You must agree before submitting');

  const schema = React.useMemo(
    () =>
      yup.object().shape({
        notes: fieldValidation(4000),
        location: dropDownValidation,
        serviceType: dropDownValidation,
        sessionStatus: dropDownValidation,
        serviceProvider: dropDownValidation,
        serviceDate: yup
          .date()
          .nullable()
          .required(requiredField)
          .typeError(requiredField)
          .min(new Date(new Date().setHours(0, 0, 0, 0)), disablePastDate),
        studentId: yup.string().required(requiredField),
        esignature: signatureTextValidation,
        noteSignatureAgree: signatureAgreeValidation,

        // genEdEsignature: signatureTextValidation,
        // genEdSignatureAgree: signatureAgreeValidation,
        serviceDurationMinutes: yup.string().required(requiredField).nullable(),
      }),
    [],
  );

  const [student, setStudent] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [isStudentSearchLoading, setIsStudentSearchLoading] = useState(false);

  useEffect(() => {
    if (studentSearchQuery?.length >= 1) fetchSearchedStudents();
  }, [studentSearchQuery]);

  const fetchSearchedStudents = async () => {
    try {
      setIsStudentSearchLoading(true);
      const searchedStudents = await getSearchedStudents(
        studentSearchQuery,
        true,
        false,
        20,
      );
      setStudentOptions(searchedStudents?.items || []);
      setIsStudentSearchLoading(false);
    } catch (error) {
      setIsStudentSearchLoading(false);
    }
  };

  const defaultValue = {
    notes: '',
    location: '',
    studentId: '',
    esignature: '',
    serviceType: '',
    serviceDate: null,
    sessionStatus: '',
    serviceProvider: '',
    noteSignatureAgree: false,
    serviceDurationMinutes: 30,
    serviceTime: getCurrentTime(),
    seifEsignature: '',
    seifSignatureAgree: false,
    genEdEsignature: '',
    genEdSignatureAgree: false,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(schema),
  });

  const { reset, handleSubmit, control, formState, setValue, watch } =
    useFunction;
  const { errors } = formState;

  const watchedServiceDate = watch('serviceDate');

  const {
    locations = [],
    providers = [],
    serviceTypes = [],
    sessionStatuses = [],
  } = filterOptions || {};

  const mapOptions = (arr) =>
    arr?.map((item) => ({ id: item, label: item, value: item }));

  const locationsOptions = mapOptions(locations);
  const providersOptions = mapOptions(providers);
  const serviceTypesOptions = mapOptions(serviceTypes);
  const sessionStatusesOptions = mapOptions(sessionStatuses);

  const handleStudentSelect = (val) => {
    const studentId = val?.studentId || '';
    setStudent(val);
    setValue('studentId', studentId, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleClose = () => {
    setStudent(null);
    reset(defaultValue);
    setStudentOptions([]);
    setStudentSearchQuery('');
    setIsStudentSearchLoading(false);
  };

  const onSubmit = (data) => {
    const {
      studentId,
      studentIds,
      serviceDate,
      serviceTime,
      multipleStudents,
      ...restData
    } = data;

    const formatAsLocalDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      return d.toISOString().split('T')[0];
    };

    const payload = {
      ...restData,
      studentId: student?.studentId,
      serviceDate: formatServiceDateTime(serviceDate, serviceTime),
    };

    onSave(
      spedNoteSuccess,
      true,
      postSpedNote,
      () => {},
      payload,
      student?.studentId,
      () => {},
      handleClose,
      setOpenNotification,
      setNotificationMessage,
      dispatch,
      setLoadingButton,
    );
  };

  return (
    <>
      <SpedGridForm container spacing={1}>
        <SpedNoteHeading handleClose={handleClose} />

        <Grid container item>
          <Grid item xs={12}>
            <SpedPaper>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormInputAutoCompleteSearch
                    value={student}
                    label="Search student"
                    options={studentOptions}
                    error={!!errors?.studentId}
                    placeholder="Search student"
                    getOptionLabel={getOptionLabel}
                    setSelectedInput={handleStudentSelect}
                    helperText={errors?.studentId?.message}
                    isSearchloading={isStudentSearchLoading}
                    setInputSearchQuery={setStudentSearchQuery}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormInputDate
                    control={control}
                    name="serviceDate"
                    label="Service Date"
                    minDate={currentDate}
                    value={watchedServiceDate}
                    errors={!!errors?.serviceDate}
                    helperText={errors?.serviceDate?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormInputText
                    type="number"
                    control={control}
                    name="serviceDurationMinutes"
                    label="Service Duration (minutes)"
                    errors={!!errors?.serviceDurationMinutes}
                    helperText={errors?.serviceDurationMinutes?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormInputText
                    type="time"
                    shrink={true}
                    control={control}
                    name="serviceTime"
                    label="Service Time"
                    errors={!!errors.serviceTime}
                    helperText={errors?.serviceTime?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormInputDropdown
                    name="location"
                    label="Location"
                    control={control}
                    options={locationsOptions}
                    errors={!!errors?.location}
                    helperText={errors?.location?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormInputDropdown
                    control={control}
                    name="serviceProvider"
                    label="Service Provider"
                    options={providersOptions}
                    errors={!!errors?.serviceProvider}
                    helperText={errors?.serviceProvider?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormInputDropdown
                    control={control}
                    name="sessionStatus"
                    label="Session Status"
                    options={sessionStatusesOptions}
                    errors={!!errors?.sessionStatus}
                    helperText={errors?.sessionStatus?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormInputDropdown
                    control={control}
                    name="serviceType"
                    label="Service Type"
                    options={serviceTypesOptions}
                    errors={!!errors?.serviceType}
                    helperText={errors?.serviceType?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormInputText
                    rows={2}
                    multiline
                    type="text"
                    name="notes"
                    label="Notes"
                    control={control}
                    errors={!!errors?.notes}
                    helperText={errors?.notes?.message}
                  />
                </Grid>
                <Grid item></Grid>
              </Grid>
            </SpedPaper>
          </Grid>
        </Grid>

        <Grid container item spacing={1}>
          <Grid item xs={12}>
            <SignatureBlock
              errors={errors}
              control={control}
              inputName="esignature"
              title="SPED Provider Signature"
              inputLabel="Electronic Signature"
              checkboxName="noteSignatureAgree"
              inputPlaceholder="Enter your full name"
            />
          </Grid>

          <Grid item xs={12}>
            <SignatureBlock
              // errors={errors}
              control={control}
              inputName="genEdEsignature"
              checkboxName="genEdSignatureAgree"
              inputLabel="Gen Ed Electronic Signature"
              title="General Ed Teacher Signature (optional)"
              inputPlaceholder="Enter Gen Ed teacher's full name"
            />
          </Grid>
        </Grid>

        {!canAccessSpedNotes && <Grid item xs={12} sm={6}></Grid>}
        {canAccessSpedNotes &&
          renderSaveCancelButtons(
            handleClose,
            handleSubmit(onSubmit),
            loadingButton,
          )}
      </SpedGridForm>
      <NotificationPopup />
    </>
  );
};

export default AddSpedNote;
